import { useMemo } from 'react';
import { usePermissions } from '@aegix/auth-permissions';

/**
 * Multi-permission gate — returns true iff every codename in
 * `allowedPermissions` is present in the current user's permissions.
 *
 * Empty `allowedPermissions` → unconditionally true (no gate).
 *
 * Internally uses `usePermissions()` from `@aegix/auth-permissions`
 * once and a memoized `Set` so the `.every()` lookup is O(n) where
 * n = `allowedPermissions.length`, not O(n × m). Matches the
 * single-codename `useHasPermission` hook's cost model.
 */
export const useHasPermissions = (allowedPermissions: string[]) => {
  const permissions = usePermissions();
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  if (allowedPermissions.length === 0) {
    return true;
  }
  return allowedPermissions.every((p) => permissionSet.has(p));
};
